import { useMemo, useState } from "react";
import { Check, Tags, X } from "lucide-react";
import { NotificationModal } from "@/components/modals";
import TaxonomyPicker from "@/components/courses/TaxonomyPicker";
import {
  COURSE_DIALOG_STYLE,
  type TaxonomyOption,
} from "@/components/courses/courseMeta";
import "@/components/courses/CourseMetaFields.css";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string) {
  return UUID_RE.test(value);
}

function sameIds(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const other = new Set(b);
  return a.every((id) => other.has(id));
}

function labelFor(id: string, options: TaxonomyOption[], loading: boolean) {
  const row = options.find((item) => String(item.id) === id);
  if (row) return row.shortTitle || row.title;
  if (!isUuid(id)) return id;
  return loading ? "…" : "Unknown standard";
}

export default function ContainerStandardsField({
  label,
  selectedIds,
  options,
  scopeOptions,
  loading,
  scopeId,
  scopeTitle,
  disabled = false,
  saved = false,
  onSave,
  onOptionsChange,
}: {
  label: string;
  selectedIds: string[];
  options: TaxonomyOption[];
  scopeOptions: TaxonomyOption[];
  loading: boolean;
  scopeId: string;
  scopeTitle?: string | null;
  disabled?: boolean;
  saved?: boolean;
  onSave: (ids: string[]) => void;
  onOptionsChange?: (options: TaxonomyOption[]) => void;
}) {
  const catalogIds = useMemo(() => selectedIds.filter(isUuid), [selectedIds]);
  const [open, setOpen] = useState(false);
  const [draftIds, setDraftIds] = useState<string[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);

  const openPicker = () => {
    if (disabled) return;
    setDraftIds(catalogIds);
    setOpen(true);
  };

  const closePicker = () => {
    if (editorOpen) return;
    setOpen(false);
  };

  const commit = (ids: string[]) => {
    const next = ids.filter(isUuid);
    if (!sameIds(next, selectedIds.filter(isUuid))) onSave(next);
  };

  const remove = (id: string) => {
    onSave(selectedIds.filter((item) => item !== id));
  };

  const chips = catalogIds.map((id) => ({
    id,
    label: labelFor(id, options, loading),
  }));

  const placeholder = scopeId
    ? "Search standards…"
    : "Set a course subject to tag standards";

  return (
    <div>
      <label className="al-cb-label">{label}</label>
      <span className="al-cb-inline-wrap">
        <div
          className={`al-cb-std-field${chips.length ? " al-cb-std-field--on" : ""}${disabled ? " al-cb-std-field--disabled" : ""}`}
        >
          {chips.length ? (
            <span className="al-cb-std-chips" onClick={disabled ? undefined : openPicker}>
              {chips.map((chip) => (
                <span
                  key={chip.id}
                  className="al-cb-std-chip"
                >
                  <span className="al-cb-std-chip-label">{chip.label}</span>
                  {disabled ? null : (
                    <button
                      type="button"
                      className="al-cb-std-chip-x"
                      aria-label={`Remove ${chip.label}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        remove(chip.id);
                      }}
                    >
                      <X size={10} aria-hidden="true" />
                    </button>
                  )}
                </span>
              ))}
            </span>
          ) : (
            <button
              type="button"
              className="al-cb-std-placeholder-btn"
              disabled={disabled}
              aria-haspopup="dialog"
              aria-expanded={open}
              onClick={openPicker}
            >
              {placeholder}
            </button>
          )}
          <button
            type="button"
            className="al-cb-std-open"
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={chips.length ? `Edit ${label.toLowerCase()}` : label}
            title={chips.length ? `Edit ${label.toLowerCase()}` : label}
            onClick={openPicker}
          >
            <Tags size={14} aria-hidden="true" />
          </button>
        </div>
        {saved ? (
          <span className="al-cb-saved" title="Saved">
            <Check size={13} />
          </span>
        ) : null}
      </span>

      <NotificationModal
        isOpen={open}
        type="neutral"
        icon={<Tags />}
        title={label}
        message={
          scopeTitle
            ? `From the course subject · ${scopeTitle}`
            : "Pick a subject on the course first — standards are filtered to that catalog."
        }
        overlay
        overlayClose={!editorOpen}
        escapeClose={!editorOpen}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1250}
        style={COURSE_DIALOG_STYLE}
        onClose={closePicker}
      >
        <div className="al-courses-dialog-form">
          <TaxonomyPicker
            catalog="standard"
            multiple
            selectedIds={draftIds}
            options={options}
            loading={loading}
            disabled={disabled}
            scopeId={scopeId}
            scopeOptions={scopeOptions}
            onSelectIds={setDraftIds}
            onOptionsChange={onOptionsChange}
            onEditorOpenChange={setEditorOpen}
          />
          <div className="al-courses-assign-actions">
            <button type="button" className="al-courses-btn al-courses-btn--ghost" onClick={closePicker}>
              Cancel
            </button>
            <button
              type="button"
              className="al-courses-btn"
              disabled={disabled || !scopeId}
              onClick={() => {
                commit(draftIds);
                setOpen(false);
              }}
            >
              Done
            </button>
          </div>
        </div>
      </NotificationModal>
    </div>
  );
}
